import { MEETING_IMPORT_CONTRACT as contract } from './meetingImportContract.ts';

/** Shared import contract. Source and canonical quotes are server authored; review edits never replace them. */
export type MeetingSource =
  | { kind: 'text'; text: string; name?: string }
  | { kind: 'url'; url: string }
  | { kind: 'media'; path: string; name: string; mimeType: string; size: number };
export interface MeetingCandidate {
  id: string; kind: 'task' | 'note'; title: string; body: string;
  people: string[]; dueDate: string | null; quote: string; selected: boolean;
  /** Unicode code-point offsets into the complete transcript; end is exclusive. */
  sourceStart: number; sourceEnd: number;
}
export interface MeetingImport {
  id: string; user_id: string; source: MeetingSource; transcript: string | null;
  status: 'pending' | 'extracting' | 'review' | 'filed';
  candidates: MeetingCandidate[]; next_chunk: number; error: string | null;
  receipt: unknown | null; created_at: string;
  /** Provider/persisted RFC3339 timestamp, retaining its offset and fractional precision. */
  meeting_date?: string | null;
}
export type MeetingCommitItem = MeetingCandidate & { personIds?: string[] };
export interface MeetingTranscriptChunk { start: number; end: number; text: string }
export const MEETING_MAX_TRANSCRIPT_CHARS = contract.max_transcript_chars;
export const MEETING_MAX_MEDIA_BYTES = contract.max_media_bytes;
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const record = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const textLength = (value: string): number => Array.from(value).length;
const bounded = (v: unknown, max: number, blank = false): v is string => typeof v === 'string' && textLength(v) <= max && (blank || v.trim().length > 0);
const date = (v: unknown): v is string | null => v === null || (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`)) && new Date(`${v}T00:00:00Z`).toISOString().slice(0, 10) === v);
export function validateMeetingDate(value: unknown): string {
  if (typeof value !== 'string'
    || !/^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,6})?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/.test(value)
    || !date(value.slice(0, 10)) || !Number.isFinite(Date.parse(value))) throw new Error('Invalid meeting date');
  return value;
}
/** Storage SDK paths are transport-only; immutable source and server metadata stay logical. */
export const meetingStoragePath = (path: string): string => path.split('/').map(encodeURIComponent).join('/');
const chunkBoundary = (maxChars: number, overlapChars: number) => Math.max(overlapChars + 1, Math.floor(maxChars / 2));
// A natural boundary can end at boundary+1; a full window can end earlier for tiny custom bounds.
export const MEETING_MAX_TRANSCRIPT_CHUNKS = Math.ceil(contract.max_transcript_chars /
  (Math.min(contract.chunk_max_chars, chunkBoundary(contract.chunk_max_chars, contract.chunk_overlap_chars) + 1) - contract.chunk_overlap_chars));

export function validateMeetingTranscript(text: unknown): string {
  if (!bounded(text, MEETING_MAX_TRANSCRIPT_CHARS)) throw new Error(`Transcript must contain text and be at most ${MEETING_MAX_TRANSCRIPT_CHARS.toLocaleString('en-US')} characters`);
  return text;
}

export function validateMeetingSource(raw: unknown, ownerId?: string, importId?: string): MeetingSource {
  if (!record(raw)) throw new Error('Invalid meeting source');
  if (raw.kind === 'text') {
    const text = validateMeetingTranscript(raw.text);
    if (raw.name !== undefined && !bounded(raw.name, contract.max_filename_chars)) throw new Error('Invalid transcript filename');
    return { kind: 'text', text, ...(raw.name === undefined ? {} : { name: raw.name as string }) };
  }
  if (raw.kind === 'url') {
    if (!bounded(raw.url, contract.max_url_chars)) throw new Error('Invalid meeting URL');
    const url = new URL(raw.url);
    if (url.protocol !== 'https:' || url.username || url.password) throw new Error('Meeting URLs require HTTPS without credentials');
    return { kind: 'url', url: raw.url };
  }
  if (raw.kind === 'media') {
    const extension = typeof raw.name === 'string' ? raw.name.match(/\.([^.]+)$/)?.[1]?.toLowerCase() : undefined;
    if (!ownerId || !importId || !uuid.test(ownerId) || !uuid.test(importId) || !bounded(raw.name, contract.max_filename_chars) || /[/\\]/.test(raw.name) || !contract.media_extensions.some(allowed => allowed === extension)
      || !bounded(raw.path, contract.max_path_chars) || raw.path !== `${ownerId}/${importId}/${raw.name}`
      || !contract.media_mime_types.some(allowed => allowed === raw.mimeType)
      || typeof raw.size !== 'number' || !Number.isSafeInteger(raw.size) || raw.size <= 0 || raw.size > MEETING_MAX_MEDIA_BYTES) throw new Error(`Media must use an owned import path, a supported recording format and be at most ${MEETING_MAX_MEDIA_BYTES / 1_000_000} MB`);
    return { kind: 'media', path: raw.path, name: raw.name, mimeType: raw.mimeType as string, size: raw.size };
  }
  throw new Error('Unsupported meeting source');
}

/** Full offsets refer to the untouched original, including whitespace and the final character. */
export function chunkMeetingTranscript(text: string, maxChars: number = contract.chunk_max_chars, overlapChars: number = contract.chunk_overlap_chars): MeetingTranscriptChunk[] {
  validateMeetingTranscript(text);
  if (!Number.isSafeInteger(maxChars) || !Number.isSafeInteger(overlapChars) || maxChars < 2 || overlapChars < 1 || overlapChars >= maxChars) throw new Error('Invalid chunk bounds');
  const chars = Array.from(text);
  const chunks: MeetingTranscriptChunk[] = [];
  let start = 0;
  while (start < chars.length) {
    let end = Math.min(start + maxChars, chars.length);
    if (end < chars.length) {
      const lower = start + chunkBoundary(maxChars, overlapChars);
      for (let i = end - 1; i >= lower; i--) if (/[\n.!?]/.test(chars[i])) { end = i + 1; break; }
    }
    chunks.push({ start, end, text: chars.slice(start, end).join('') });
    if (end === chars.length) break;
    start = end - overlapChars;
  }
  return chunks;
}

function candidate(raw: unknown): MeetingCandidate {
  if (!record(raw) || !bounded(raw.id, contract.max_candidate_id_chars) || !['task', 'note'].includes(String(raw.kind)) || !bounded(raw.title, contract.max_title_chars) || !bounded(raw.body, contract.max_body_chars, true)
    || !bounded(raw.quote, contract.max_quote_chars) || !Array.isArray(raw.people) || raw.people.length > contract.max_people_per_item || !raw.people.every(p => bounded(p, contract.max_person_name_chars))
    || !date(raw.dueDate) || typeof raw.selected !== 'boolean' || typeof raw.sourceStart !== 'number' || typeof raw.sourceEnd !== 'number' || !Number.isSafeInteger(raw.sourceStart) || !Number.isSafeInteger(raw.sourceEnd)
    || raw.sourceStart < 0 || raw.sourceEnd <= raw.sourceStart || raw.sourceEnd - raw.sourceStart !== textLength(raw.quote)) throw new Error('Invalid meeting candidate');
  return { id: raw.id, kind: raw.kind as 'task' | 'note', title: raw.title, body: raw.body, people: raw.people as string[], dueDate: raw.dueDate, quote: raw.quote, selected: raw.selected, sourceStart: raw.sourceStart, sourceEnd: raw.sourceEnd };
}
const key = (c: MeetingCandidate): string => JSON.stringify([c.sourceStart, c.sourceEnd, c.kind, c.title, c.quote]);
export function mergeMeetingCandidates(existing: MeetingCandidate[], incoming: MeetingCandidate[]): MeetingCandidate[] {
  const result: MeetingCandidate[] = [], seen = new Set<string>(), ids = new Set<string>();
  for (const item of [...existing, ...incoming]) {
    const c = candidate(item), k = key(c);
    if (seen.has(k)) continue;
    if (ids.has(c.id)) throw new Error('Duplicate meeting candidate ID');
    seen.add(k); ids.add(c.id); result.push(c);
  }
  if (result.length > contract.max_candidates) throw new Error('Too many meeting candidates');
  return result;
}

/** The model never chooses identities. Dates are admitted only when explicitly ISO-dated in the quote; otherwise they remain unknown. */
export function readMeetingCandidates(raw: unknown, transcript: string, idForIndex: (index: number) => string, chunkOffset = 0): MeetingCandidate[] {
  validateMeetingTranscript(transcript);
  if (!Number.isSafeInteger(chunkOffset) || chunkOffset < 0) throw new Error('Invalid transcript chunk offset');
  if (!Array.isArray(raw) || raw.length > contract.max_candidates) throw new Error('Invalid meeting candidates');
  const candidates = raw.map((item, index) => {
    if (!record(item)) throw new Error('Invalid meeting candidate');
    if (!bounded(item.quote, contract.max_quote_chars)) throw new Error('Invalid meeting quote');
    const position = transcript.indexOf(item.quote);
    if (position < 0) throw new Error('Meeting quote does not occur in its source');
    if (transcript.indexOf(item.quote, position + 1) !== -1) throw new Error('Ambiguous repeated passage: provide a fuller supporting quote');
    const sourceStart = chunkOffset + textLength(transcript.slice(0, position));
    const c = candidate({ ...item, id: idForIndex(index), sourceStart, sourceEnd: sourceStart + textLength(item.quote) });
    if (!transcript.includes(c.quote) || c.people.some(p => {
      const escaped = p.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return !new RegExp(`(^|[^\\p{L}\\p{N}])${escaped}($|[^\\p{L}\\p{N}])`, 'iu').test(c.quote);
    }) || (c.dueDate !== null && !c.quote.includes(c.dueDate))) throw new Error('Meeting candidate is not grounded in its source quote');
    return c;
  });
  return mergeMeetingCandidates([], candidates);
}

export function validateMeetingItems(raw: unknown, canonicalCandidates: MeetingCandidate[]): MeetingCommitItem[] {
  if (!Array.isArray(raw) || !raw.length || raw.length > contract.max_candidates) throw new Error('Choose meeting candidates');
  if (!raw.some(item => record(item) && item.selected === true)) throw new Error('Select at least one meeting item');
  const canonical = new Map(canonicalCandidates.map(c => [c.id, c]));
  const seen = new Set<string>();
  return raw.map(item => {
    const c = candidate(item), original = canonical.get(c.id);
    if (!original || original.quote !== c.quote || original.kind !== c.kind || original.sourceStart !== c.sourceStart || original.sourceEnd !== c.sourceEnd || seen.has(c.id)) throw new Error('Meeting candidate provenance changed or duplicate ID');
    seen.add(c.id);
    const personIds = (item as Record<string, unknown>).personIds;
    if (personIds !== undefined && (!Array.isArray(personIds) || personIds.length > contract.max_people_per_item || !personIds.every(id => typeof id === 'string' && uuid.test(id)) || new Set(personIds).size !== personIds.length)) throw new Error('Invalid person IDs');
    return { ...c, ...(personIds === undefined ? {} : { personIds: personIds as string[] }) };
  });
}
